<div
  class="reservation-popover"
  (click)="$event.stopPropagation()"
  (mouseenter)="onMouseEnter()"
  (mouseleave)="onMouseLeave()">
  <div class="popover-header">
    <div
      class="avatar"
      [class.has-logo]="sourceLogoUrl || guestProfilePicture"
      [style.background]="(sourceLogoUrl || guestProfilePicture) ? '#ffffff' : avatarGradient">
      @if (guestProfilePicture) {
        <img class="avatar-logo profile-pic" [src]="guestProfilePicture" alt="" (error)="onProfilePictureError()" />
      } @else if (sourceLogoUrl) {
        <img class="avatar-logo" [src]="sourceLogoUrl" alt="" (error)="onLogoError()" />
      } @else {
        <span>{{ initials }}</span>
      }
    </div>
    <div class="header-text">
      @if (isInquiry) {
        <tidy-text [header]="inquiryTitle" class="guest-name" />
        <tidy-text [helper]="inquirySubtitle" class="status-line inquiry-subtitle" />
      } @else {
        <tidy-text [header]="headerTitle" class="guest-name" />
        <tidy-text [helper]="statusLabel" class="status-line" />
      }
      @if (address) {
        <tidy-text [helper]="address" class="status-line address-line" />
      }
    </div>
  </div>

  @if (hasGuestInfo && !isInquiry) {
    <tidy-card class="secondary-layer no-margin">
      @if (guestRows.length) {
        @for (guest of guestRows; track guest.id) {
          <div class="guest-row">
            <tidy-image [src]="'assets/svg/person.svg'" class="guest-icon" />
            <div class="guest-row-text">
              <div class="guest-name-line">
                <tidy-text [body]="guest.name" class="guest-name-text bold" />
                @if (guest.isBooker) {
                  <tidy-text [smallHelper]="'Booker'" class="guest-badge" />
                }
              </div>
              @if (guest.email) {
                <tidy-text [helper]="guest.email" />
              }
              @if (guest.phone) {
                <tidy-text [helper]="guest.phone" />
              }
            </div>
          </div>
        }
      } @else if (guestFullName) {
        <div class="guest-row">
          <tidy-image [src]="'assets/svg/person.svg'" class="guest-icon" />
          <tidy-text [body]="guestFullName" class="guest-name-text bold" />
        </div>
      }
      @if (notes) {
        <div class="info-row notes-row">
          <tidy-image [src]="'assets/svg/information-circle-outline.svg'" class="info-icon" />
          <tidy-text [body]="notes" />
        </div>
      }
    </tidy-card>
  }

  @if (reservation?.state === 'quote' && (formattedQuoteAmount || quoteAgeLabel || holdTimeRemainingLabel || quoteExpiresInHours !== null)) {
    <tidy-card class="secondary-layer no-margin state-banner-alert quote" [class.urgent]="quoteExpiresSoon || quoteExpired">
      <div class="state-banner-head">
        <tidy-image [src]="'assets/svg/pricetag-outline.svg'" class="state-banner-icon" />
        <tidy-text [body]="'Quote sent'" class="state-banner-title" />
        @if (formattedQuoteAmount) {
          <tidy-text [body]="formattedQuoteAmount" class="state-banner-amount" />
        }
      </div>
      <div class="state-banner-meta">
        @if (quoteAgeLabel) {
          <tidy-text [helper]="('Sent ' | translate) + quoteAgeLabel" />
        }
        @if (quoteExpired) {
          <tidy-text [helper]="'Expired'" class="state-banner-alert" />
        } @else if (quoteExpiresInHours !== null) {
          <tidy-text
            [helper]="('Expires in ' | translate) + quoteExpiresInHours + ('h' | translate)"
            [class.state-banner-alert]="quoteExpiresSoon" />
        }
        @if (reservation?.quoteViewedAt) {
          <tidy-text [helper]="'Viewed'" />
        }
      </div>
    </tidy-card>
  }

  @if (reservation?.state === 'hold') {
    <div class="state-banner hold" [class.urgent]="holdExpiresSoon">
      <div class="state-banner-head">
        <tidy-image [src]="'assets/svg/time-outline.svg'" class="state-banner-icon" />
        <tidy-text [body]="holdBannerTitle" class="state-banner-title" />
        @if (holdTimeRemainingLabel) {
          <tidy-text [body]="holdTimeRemainingLabel" class="state-banner-amount" />
        }
      </div>
      <div class="state-banner-meta">
        @if (reservation?.holdType) {
          @if (depositStatusLabel) {
            <tidy-text [helper]="depositStatusLabel" />
          }
          @if (formattedDepositAmount) {
            <tidy-text [helper]="formattedDepositAmount" />
          }
        } @else {
          <tidy-text [helper]="'These dates are held until the guest confirms or the hold expires.'" />
        }
      </div>
    </div>
  }

  @if (isInquiry && (needsResponse || reservation?.followUpCount)) {
    <div class="inquiry-banner" [class.urgent]="needsResponse">
      <span class="inquiry-banner-icon-wrap">
        <tidy-image [src]="'assets/svg/chatbubble-outline.svg'" class="inquiry-banner-icon" />
      </span>
      <div class="inquiry-banner-text">
        <tidy-text [body]="inquiryBannerTitle" class="inquiry-banner-title" />
        @if (inquiryBannerHint) {
          <tidy-text [helper]="inquiryBannerHint" class="inquiry-banner-hint" />
        }
      </div>
    </div>
  }

  @if (reservation?.messageText) {
    <tidy-card class="secondary-layer no-margin">
      <div class="message-row">
        <tidy-image [src]="'assets/svg/chatbubble-outline.svg'" class="message-icon" />
        <tidy-text [body]="reservation.messageText" />
      </div>
    </tidy-card>
  }

  @if (reservation?.checkInTime || reservation?.checkOutTime) {
    <div class="times-row">
      @if (reservation?.checkInTime) {
        <tidy-card class="secondary-layer no-margin time-chip">
          <tidy-image [src]="'assets/svg/log-in-outline.svg'" class="time-icon" />
          <div class="time-text">
            <tidy-text [smallHelper]="'Check in'" class="time-label" />
            <tidy-text [body]="reservation.checkInTime" class="time-value" />
          </div>
        </tidy-card>
      }
      @if (reservation?.checkOutTime) {
        <tidy-card class="secondary-layer no-margin time-chip">
          <tidy-image [src]="'assets/svg/log-out-outline.svg'" class="time-icon" />
          <div class="time-text">
            <tidy-text [smallHelper]="'Check out'" class="time-label" />
            <tidy-text [body]="reservation.checkOutTime" class="time-value" />
          </div>
        </tidy-card>
      }
    </div>
  }

  @if (isLoadingLockCodes() || lockCodes().length) {
    <tidy-card class="secondary-layer no-margin">
      @if (isLoadingLockCodes()) {
        <div class="lock-code-row">
          <tidy-image [src]="'assets/svg/key-outline.svg'" class="lock-code-icon" />
          <div class="lock-code-text">
            <tidy-text [smallHelper]="'Lock Code'" />
            <ion-skeleton-text [animated]="true" class="lock-code-skeleton" />
          </div>
        </div>
      } @else {
        @for (item of lockCodes(); track item.deviceId) {
          <div class="lock-code-row">
            <tidy-image [src]="'assets/svg/key-outline.svg'" class="lock-code-icon" />
            <div class="lock-code-text">
              <tidy-text [smallHelper]="item.label" />
              <tidy-text [body]="item.code" class="lock-code-value bold" />
            </div>
            <tidy-text
              [actionBody]="'Copy'"
              (action)="copyLockCode(item.code)"
              class="link" />
          </div>
        }
      }
    </tidy-card>
  }

  @if (bookedOnLabel) {
    <tidy-card class="secondary-layer no-margin">
      <div class="booked-on-row">
        <tidy-image [src]="'assets/svg/calendar-outline.svg'" class="booked-icon" />
        <tidy-text [body]="bookedOnLabel" />
      </div>
    </tidy-card>
  }

  @if (guestCounts.length) {
    <div class="counts-row">
      @for (g of guestCounts; track g.label) {
        <span class="count-chip">
          <tidy-image [src]="g.icon" class="count-icon" />
          <tidy-text [body]="g.count + ' ' + g.label" />
        </span>
      }
    </div>
  }

  @if (isInquiry && inquiryPayout) {
    <div class="inquiry-payout">
      <div class="payout-row">
        <tidy-text [helper]="'Potential payout'" class="payout-label" />
        <tidy-text [header]="inquiryPayout" class="payout-value" />
      </div>
      <div class="payout-note-row">
        <tidy-image [src]="'assets/svg/information-circle-outline.svg'" class="payout-note-icon" />
        <tidy-text [smallHelper]="'Nothing is charged unless the guest books'" class="payout-note" />
      </div>
    </div>
  }

  @if (hasFinancials && !isInquiry) {
    <tidy-card class="secondary-layer no-margin">
      <div class="financials">
        @if (formattedTotal) {
          <div class="total-row">
            <tidy-text [helper]="'Total'" class="total-label" />
            <tidy-text [header]="formattedTotal" class="total-value" />
          </div>
        }
        @if (reservation?.channelBreakdown; as breakdown) {
          <div class="pricing-breakdown">
            @for (row of breakdown.guestRows; track $index) {
              <div class="pricing-row">
                <tidy-text [helper]="row.label" class="pricing-label" />
                <tidy-text [body]="row.value" class="pricing-value" />
              </div>
            }
          </div>
          @if (breakdown.platformRows.length) {
            <div class="pricing-breakdown">
              <tidy-text [helper]="breakdown.platformTitle" class="pricing-group-title" />
              @for (row of breakdown.platformRows; track $index) {
                <div class="pricing-row">
                  <tidy-text [helper]="row.label" class="pricing-label" />
                  <tidy-text [body]="row.value" class="pricing-value" />
                </div>
              }
            </div>
          }
          <div class="total-row">
            <tidy-text [helper]="'Your payout'" class="total-label" />
            <tidy-text [header]="breakdown.payoutValue" class="total-value" />
          </div>
        } @else if (pricingRows.length) {
          <div class="pricing-breakdown">
            @for (row of pricingRows; track row.label) {
              <div class="pricing-row">
                <tidy-text [helper]="row.label" class="pricing-label" />
                <tidy-text [body]="row.value" class="pricing-value" />
              </div>
            }
          </div>
        }
        @if (reservation?.total) {
          <div class="progress-track">
            <div class="progress-fill" [style.width.%]="paidPercent"></div>
          </div>
        }
        <div class="paid-owes-row">
          @if (formattedPaid) {
            <tidy-text [body]="('Paid ' | translate) + formattedPaid" class="paid-chip" />
          }
          @if (hasDues && formattedOwes) {
            <tidy-text [body]="('Owes ' | translate) + formattedOwes" class="owes-chip" />
          }
        </div>
      </div>
    </tidy-card>
  }

  @if (reviewStatusLabel) {
    <div class="review-row">
      <tidy-image [src]="'assets/svg/star-outline.svg'" class="review-icon" />
      <tidy-text [helper]="reviewStatusLabel" />
    </div>
  }

  <div class="actions-row">
    @if (hasGuidebook && !isInquiry) {
      <tidy-button
        class="secondary"
        [smallBody]="true"
        [text]="'Guidebook'"
        [image]="'assets/svg/book-outline.svg'"
        [action]="handleGuidebookClick.bind(this)" />
    }
    @if (canMessage) {
      <tidy-button
        class="secondary"
        [smallBody]="true"
        [text]="'Message'"
        [image]="'assets/svg/chatbubble-outline.svg'"
        [action]="handleMessageClick.bind(this)" />
    }
    <tidy-button
      class="primary"
      [smallBody]="true"
      [text]="isInquiry ? 'Respond' : 'View Details'"
      [action]="handleDetailsClick.bind(this)" />
  </div>
</div>
